import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import { 
  FiArrowLeft, 
  FiPlus, 
  FiSearch, 
  FiLayers, 
  FiFileText, 
  FiTrash2, 
  FiChevronDown, 
  FiChevronLeft, 
  FiChevronRight,
  FiZap,
  FiLayout,
  FiPercent,
  FiBarChart2,
  FiGrid,
  FiSettings,
  FiRefreshCcw,
  FiCheck,
  FiX,
  FiCloud,
  FiDownload,
} from 'react-icons/fi';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { testService } from '../../../services/testService';
import toast from 'react-hot-toast';
import { useAssessmentContext } from '../AssessmentContext';
import '../assessment-module-mobile.css';
import PedagogyDropdownMenu from '../QuestionBank/PedagogyDropdownMenu';
import PedagogyFieldHeader from '../QuestionBank/PedagogyFieldHeader';
import { AssessmentEditIcon, assessTipProps } from '../AssessmentContext';

const ASSESSMENT_ACTION_ICON_BTN_CLASS =
  'w-9 h-9 rounded-xl flex items-center justify-center border transition-all shadow-sm shrink-0';

const AssessmentConfirmIconButton = ({
  onClick,
  title = 'Confirm',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-red-100 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white ${className}`}
  >
    <FiCheck className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentCancelIconButton = ({
  onClick,
  title = 'Cancel',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-slate-100 bg-slate-50 text-slate-500 hover:bg-slate-200 hover:text-slate-700 ${className}`}
  >
    <FiX className="w-4 h-4 shrink-0" strokeWidth={2.5} />
  </button>
);

const AssessmentEditIconButton = ({
  onClick,
  title = 'Edit',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 ${className}`}
  >
    <AssessmentEditIcon className="w-4 h-4 shrink-0" />
  </button>
);

const AssessmentDeleteIconButton = ({
  onClick,
  title = 'Delete',
  className = '',
  type = 'button',
}) => (
  <button
    type={type}
    onClick={onClick}
    {...assessTipProps(title)}
    className={`${ASSESSMENT_ACTION_ICON_BTN_CLASS} border-rose-100 bg-rose-50 text-rose-500 hover:bg-[#b91c1c] hover:text-white ${className}`}
  >
    <FiTrash2 className="w-4 h-4 shrink-0" strokeWidth={2} />
  </button>
);

const MCQ_OPTION_LETTERS = ['A', 'B', 'C', 'D'];

const isMcqQuestionType = (type) => String(type ?? '').toUpperCase() === 'MCQ';

const getMcqOptionText = (question, letter) => {
  const upper = String(letter ?? '').toUpperCase();
  const lower = upper.toLowerCase();
  if (!MCQ_OPTION_LETTERS.includes(upper)) return '';
  return String(question?.[`option_${lower}`] ?? question?.[`option_${upper}`] ?? '').trim();
};

const resolveMcqCorrectLetter = (question) => {
  const raw = String(question?.correct_answer ?? '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  if (MCQ_OPTION_LETTERS.includes(upper)) return upper;

  const letterWithPunctuation = upper.match(/^([A-D])[\.\)\:\;\s-]*$/);
  if (letterWithPunctuation) return letterWithPunctuation[1];

  const rawLower = raw.toLowerCase();
  return (
    MCQ_OPTION_LETTERS.find((letter) => {
      const optionText = getMcqOptionText(question, letter).toLowerCase();
      return optionText !== '' && optionText === rawLower;
    }) || ''
  );
};

const isMcqCorrectOption = (question, letter) =>
  resolveMcqCorrectLetter(question) === String(letter ?? '').toUpperCase();

const normalizeMcqQuestionRow = (question) => {
  if (!isMcqQuestionType(question?.type)) return question;
  const resolved = resolveMcqCorrectLetter(question);
  return resolved ? { ...question, correct_answer: resolved } : question;
};

const normalizeQuestionForPaper = (question, section, sectionIndex) => {
  const normalizedType = String(question?.type || '').toUpperCase();
  const base = {
    ...question,
    question: (question?.question || '').replace(/\s*\(Q\d+\)\s*$/gi, '').trim(),
    template_section: section.name,
    section_idx: sectionIndex,
    marks: Number(section.marks) || 1,
    template_marks: section.marks,
    type: normalizedType === 'DESCRIPTIVE' ? 'DESCRIPTIVE' : 'MCQ',
  };

  if (isMcqQuestionType(base.type)) {
    const resolved = resolveMcqCorrectLetter(base);
    if (resolved) base.correct_answer = resolved;
  }

  return base;
};

const QB_CSV_HEADERS = [
  'section_name',
  'question',
  'type',
  'option_a',
  'option_b',
  'option_c',
  'option_d',
  'correct_answer',
  'marks',
  'expected_answer',
  'pedagogy',
];

const QB_CSV_INSTRUCTION =
  '# type: MCQ or Descriptive question. MCQ: fill options and correct_answer (A-D). Descriptive: leave options empty; put the model answer in correct_answer (and expected_answer if used).';

const DEFAULT_PEDAGOGY_OPTIONS = [
  'Algorithm Design',
  'Analytical Thinking',
  'Aptitude',
  'Code Efficiency',
  'Communication Skills',
  'Database Knowledge',
  'Data Interpretation',
  'Debugging',
  'Logical Reasoning',
];

const escapeCsvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const normalizeQuestionBankHeaderKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const validateQuestionBankHeaderRow = (headerRow) => {
  const trimmed = headerRow.map((cell) => String(cell ?? '').trim());
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop();
  }

  if (trimmed.length !== QB_CSV_HEADERS.length) {
    return {
      valid: false,
      error: `Invalid column count (${trimmed.length}). Use the downloaded template with exactly these columns in order: ${QB_CSV_HEADERS.join(', ')}.`,
    };
  }

  const mismatches = [];
  trimmed.forEach((header, index) => {
    const expected = QB_CSV_HEADERS[index];
    if (normalizeQuestionBankHeaderKey(header) !== normalizeQuestionBankHeaderKey(expected)) {
      mismatches.push(`column ${index + 1} must be "${expected}" (found "${header}")`);
    }
  });

  if (mismatches.length > 0) {
    return {
      valid: false,
      error: `Invalid column names. ${mismatches.join('; ')}. Download the template and keep the header row unchanged.`,
    };
  }

  return { valid: true };
};

const parseQuestionBankCsvLine = (line) => {
  const result = [];
  let curValue = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(curValue.trim());
      curValue = '';
    } else {
      curValue += char;
    }
  }
  result.push(curValue.trim());
  return result;
};

const normalizeQuestionBankCsvType = (typeRaw) => {
  const type = String(typeRaw || '').trim().toLowerCase();
  if (
    type === 'short answer' ||
    type === 'descriptive' ||
    type === 'descriptive question' ||
    type.includes('descriptive')
  ) {
    return 'DESCRIPTIVE';
  }
  return 'MCQ';
};

const rowToQuestionBankItem = (cols, rowIndex) => {
  const cells = QB_CSV_HEADERS.map((_, index) => String(cols[index] ?? '').trim());
  const question = cells[1];
  if (!question) return null;

  const type = normalizeQuestionBankCsvType(cells[2]);
  const expectedAnswer = cells[9];
  const correctAnswer = cells[7] || (type === 'DESCRIPTIVE' ? expectedAnswer : '');

  return {
    id: Date.now() + rowIndex,
    section_name: cells[0] || 'General',
    question,
    type,
    option_a: cells[3],
    option_b: cells[4],
    option_c: cells[5],
    option_d: cells[6],
    correct_answer: correctAnswer,
    marks: cells[8] || (type === 'DESCRIPTIVE' ? '2' : '1'),
    pedagogy: cells[10] || 'General',
  };
};

const parseQuestionBankTableRows = (tableRows) => {
  const dataRows = (tableRows || [])
    .map((row) => (Array.isArray(row) ? row : Object.values(row)).map((cell) => String(cell ?? '').trim()))
    .filter((row) => {
      const first = String(row[0] ?? '').trim();
      return row.some((cell) => cell !== '') && !first.startsWith('#') && !first.toLowerCase().startsWith('type:');
    });

  if (dataRows.length === 0) {
    return { success: false, error: 'File is empty. Download the template and add your questions below the header row.' };
  }

  const headerCheck = validateQuestionBankHeaderRow(dataRows[0]);
  if (!headerCheck.valid) {
    return { success: false, error: headerCheck.error };
  }

  const questions = [];
  for (let i = 1; i < dataRows.length; i += 1) {
    const item = rowToQuestionBankItem(dataRows[i], i);
    if (item) questions.push(item);
  }

  if (questions.length === 0) {
    return {
      success: false,
      error: 'No valid question rows found. Each row needs a value in the "question" column.',
    };
  }

  return { success: true, questions };
};

const parseQuestionBankCsv = (text) => {
  const lines = String(text || '').split(/\r?\n/);
  const dataLines = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    dataLines.push(line);
  });

  const tableRows = dataLines.map((line) => parseQuestionBankCsvLine(line));
  return parseQuestionBankTableRows(tableRows);
};

const parseQuestionBankExcel = (arrayBuffer) => {
  try {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return { success: false, error: 'Excel file has no worksheets.' };
    }

    const sheet = workbook.Sheets[sheetName];
    const tableRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    return parseQuestionBankTableRows(tableRows);
  } catch (err) {
    console.error(err);
    return { success: false, error: 'Could not read the Excel file. Save as .csv from the template or upload a valid .xlsx file.' };
  }
};

const TemplateBuilder = ({ onBack }) => {
  const { navigateToModule } = useAssessmentContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [banksRes, templatesRes] = await Promise.all([
        testService.getQuestionBanks(),
        testService.getTemplates()
      ]);
      
      if (banksRes.status === 'success') {
        setBanks(banksRes.banks);
      }
      if (templatesRes.status === 'success') {
        setSavedTemplates(templatesRes.templates);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [passMark, setPassMark] = useState('');
  const [isAddingSection, setIsAddingSection] = useState(true);
  const [templateSections, setTemplateSections] = useState([]);
  const [generatedPaper, setGeneratedPaper] = useState(null);
  const [activePreviewSection, setActivePreviewSection] = useState(0);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [editingSectionIdx, setEditingSectionIdx] = useState(null);
  const [editingPaperQuestionIdx, setEditingPaperQuestionIdx] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [newSection, setNewSection] = useState({
    name: 'MCQ Section',
    type: 'MCQ',
    marks: 1,
    count: 10
  });

  const templateScrollRef = useRef(null);

  const defaultNewSection = {
    name: 'MCQ Section',
    type: 'MCQ',
    marks: 1,
    count: 10
  };

  const fileInputRef = useRef(null);

  // Inline Question Editor States
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [activeTab, setActiveTab] = useState('MCQ');
  const [newQuestion, setNewQuestion] = useState('');
  const [newOptions, setNewOptions] = useState({ A: '', B: '', C: '', D: '' });
  const [correctOption, setCorrectOption] = useState(null);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState('');
  const [newMarks, setNewMarks] = useState(1);

  // Pedagogy States
  const [pedagogySearch, setPedagogySearch] = useState('');
  const [pedagogyOptions, setPedagogyOptions] = useState(DEFAULT_PEDAGOGY_OPTIONS);
  const [savingPedagogy, setSavingPedagogy] = useState(false);
  const [pedagogyDeleteMode, setPedagogyDeleteMode] = useState(false);
  const [selectedPedagogiesForDelete, setSelectedPedagogiesForDelete] = useState([]);
  const [deletingPedagogies, setDeletingPedagogies] = useState(false);
  const [showPedagogyDropdown, setShowPedagogyDropdown] = useState(false);
  const [pedagogyDropdownPos, setPedagogyDropdownPos] = useState(null);

  const pedagogyRef = useRef(null);
  const pedagogyAnchorRef = useRef(null);
  const pedagogyMenuRef = useRef(null);

  // Load Pedagogies
  useEffect(() => {
    let cancelled = false;
    const loadPedagogies = async () => {
      try {
        const res = await testService.getPedagogies();
        if (!cancelled && res?.status === 'success' && Array.isArray(res.pedagogies) && res.pedagogies.length > 0) {
          setPedagogyOptions(res.pedagogies);
        }
      } catch (err) {
        console.error('Failed to load pedagogies:', err);
      }
    };
    loadPedagogies();
    return () => {
      cancelled = true;
    };
  }, []);

  const trimmedPedagogySearch = pedagogySearch.trim();
  const filteredPedagogyOptions = pedagogyOptions.filter((opt) =>
    opt.toLowerCase().includes(trimmedPedagogySearch.toLowerCase())
  );
  const canSaveNewPedagogy =
    trimmedPedagogySearch.length > 0 &&
    !pedagogyOptions.some((opt) => opt.toLowerCase() === trimmedPedagogySearch.toLowerCase());

  const handleSelectPedagogy = useCallback((value) => {
    setPedagogySearch(value);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  }, []);

  const handleSaveNewPedagogy = useCallback(async () => {
    if (!canSaveNewPedagogy || savingPedagogy) return;
    setSavingPedagogy(true);
    try {
      const res = await testService.savePedagogy(trimmedPedagogySearch);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        } else if (res.pedagogy) {
          setPedagogyOptions((prev) => {
            const next = [...prev, res.pedagogy];
            return next.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          });
        }
        setPedagogySearch(res.pedagogy || trimmedPedagogySearch);
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        toast.success(res.created === false ? 'Pedagogy already exists' : 'Pedagogy saved');
      } else {
        toast.error(res?.message || 'Failed to save pedagogy');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save pedagogy');
    } finally {
      setSavingPedagogy(false);
    }
  }, [canSaveNewPedagogy, savingPedagogy, trimmedPedagogySearch]);

  const syncPedagogyDropdownPosition = useCallback(() => {
    const anchor = pedagogyAnchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuMaxHeight = 240;
    const gap = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuMaxHeight + gap && rect.top > menuMaxHeight + gap;
    setPedagogyDropdownPos({
      left: rect.left,
      width: rect.width,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
    });
  }, []);

  const exitPedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode(false);
    setSelectedPedagogiesForDelete([]);
  }, []);

  const togglePedagogyDeleteMode = useCallback(() => {
    setPedagogyDeleteMode((prev) => {
      const next = !prev;
      if (next) {
        setSelectedPedagogiesForDelete([]);
        setShowPedagogyDropdown(true);
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setSelectedPedagogiesForDelete([]);
      }
      return next;
    });
  }, [syncPedagogyDropdownPosition]);

  const togglePedagogyDeleteSelect = useCallback((name) => {
    setSelectedPedagogiesForDelete((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  }, []);

  const handleConfirmDeletePedagogies = useCallback(async () => {
    if (selectedPedagogiesForDelete.length === 0 || deletingPedagogies) return;
    setDeletingPedagogies(true);
    try {
      const res = await testService.deletePedagogies(selectedPedagogiesForDelete);
      if (res?.status === 'success') {
        if (Array.isArray(res.pedagogies)) {
          setPedagogyOptions(res.pedagogies);
        }
        const deletedLower = new Set(
          (res.deleted || selectedPedagogiesForDelete).map((n) => String(n).toLowerCase())
        );
        if (deletedLower.has((pedagogySearch || '').toLowerCase())) {
          setPedagogySearch('');
        }
        exitPedagogyDeleteMode();
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        const count = res.deleted?.length ?? selectedPedagogiesForDelete.length;
        toast.success(count === 1 ? 'Pedagogy removed' : `${count} pedagogies removed`);
      } else {
        toast.error(res?.message || 'Failed to delete pedagogies');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete pedagogies');
    } finally {
      setDeletingPedagogies(false);
    }
  }, [
    selectedPedagogiesForDelete,
    deletingPedagogies,
    pedagogySearch,
    exitPedagogyDeleteMode,
  ]);

  const pedagogyDropdownMenuProps = {
    deleteMode: pedagogyDeleteMode,
    selectedForDelete: selectedPedagogiesForDelete,
    onToggleDeleteSelect: togglePedagogyDeleteSelect,
    onConfirmDelete: handleConfirmDeletePedagogies,
    deletingSelected: deletingPedagogies,
    onCancelDeleteMode: exitPedagogyDeleteMode,
    canSaveNew: pedagogyDeleteMode ? false : canSaveNewPedagogy,
    savingNew: savingPedagogy,
    onSelect: handleSelectPedagogy,
    onSaveNew: handleSaveNewPedagogy,
  };

  const togglePedagogyDropdown = () => {
    setShowPedagogyDropdown((open) => {
      const next = !open;
      if (next) {
        requestAnimationFrame(() => syncPedagogyDropdownPosition());
      } else {
        setPedagogyDropdownPos(null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!showPedagogyDropdown) return undefined;
    syncPedagogyDropdownPosition();
    const onScrollOrResize = () => syncPedagogyDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showPedagogyDropdown, syncPedagogyDropdownPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inField = pedagogyRef.current?.contains(event.target);
      const inMenu = pedagogyMenuRef.current?.contains(event.target);
      if (!inField && !inMenu) {
        setShowPedagogyDropdown(false);
        setPedagogyDropdownPos(null);
        setPedagogyDeleteMode(false);
        setSelectedPedagogiesForDelete([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetModalState = () => {};

  const handleAddManualQuestion = (questionObj) => {
    const secType = questionObj.type;
    const secName = questionObj.section_name;
    const marks = Number(questionObj.marks);

    // 1. Update templateSections
    setTemplateSections((prevSections) => {
      const next = [...prevSections];
      const idx = next.findIndex(s => s.name === secName);
      if (idx > -1) {
        next[idx] = {
          ...next[idx],
          count: (Number(next[idx].count) || 0) + 1
        };
      } else {
        next.push({
          name: secName,
          type: secType,
          marks: marks,
          count: 1
        });
      }
      return next;
    });

    // 2. Update generatedPaper
    setGeneratedPaper((prevPaper) => {
      let paper = prevPaper ? JSON.parse(JSON.stringify(prevPaper)) : [];
      if (paper.length === 0) {
        if (templateSections.length > 0) {
          paper = templateSections.map(s => ({
            sectionName: s.name,
            sectionMarks: s.marks,
            sectionType: s.type,
            questions: []
          }));
        } else {
          paper = [{
            sectionName: secName,
            sectionMarks: marks,
            sectionType: secType,
            questions: []
          }];
        }
      }

      let sectionIdx = paper.findIndex(s => s.sectionName === secName);
      if (sectionIdx === -1) {
        sectionIdx = paper.length;
        paper.push({
          sectionName: secName,
          sectionMarks: marks,
          sectionType: secType,
          questions: []
        });
      }

      const newQ = {
        ...questionObj,
        id: Date.now(),
        template_section: secName,
        section_idx: sectionIdx,
        marks: marks,
        type: secType
      };

      paper[sectionIdx].questions.push(newQ);
      return paper;
    });

    toast.success("Question added successfully!");
  };

  const handleAddQuestion = () => {
    let currentSectionType = 'MCQ';
    let currentSectionMarks = 1;
    if (generatedPaper && generatedPaper[activePreviewSection]) {
      currentSectionType = generatedPaper[activePreviewSection].sectionType;
      currentSectionMarks = Number(generatedPaper[activePreviewSection].sectionMarks) || (currentSectionType === 'MCQ' ? 1 : 2);
    } else if (templateSections && templateSections[activePreviewSection]) {
      currentSectionType = templateSections[activePreviewSection].type;
      currentSectionMarks = Number(templateSections[activePreviewSection].marks) || (currentSectionType === 'MCQ' ? 1 : 2);
    }
    setActiveTab(currentSectionType);
    setNewMarks(currentSectionMarks);
    setEditingQuestionId(null);
    setIsAddingQuestion(true);
    setPedagogySearch('');
    setCorrectOption(null);
    setNewQuestion('');
    setNewOptions({ A: '', B: '', C: '', D: '' });
    setNewCorrectAnswer('');
  };

  const handleManualSave = () => {
    if (!newQuestion.trim()) return toast.error("Question text is required");
    if (activeTab === 'MCQ') {
      if (!newOptions.A || !newOptions.B || !newOptions.C || !newOptions.D) return toast.error("All options are required for MCQ");
      if (!correctOption) return toast.error("Please select a correct option");
    } else {
      if (!newCorrectAnswer.trim()) return toast.error("Expected answer is required for descriptive questions");
    }

    let secName = 'General';
    let secType = 'MCQ';
    let marks = 1;
    if (generatedPaper && generatedPaper[activePreviewSection]) {
      secName = generatedPaper[activePreviewSection].sectionName;
      secType = generatedPaper[activePreviewSection].sectionType;
      marks = Number(generatedPaper[activePreviewSection].sectionMarks) || (secType === 'MCQ' ? 1 : 2);
    } else if (templateSections && templateSections[activePreviewSection]) {
      secName = templateSections[activePreviewSection].name;
      secType = templateSections[activePreviewSection].type;
      marks = Number(templateSections[activePreviewSection].marks) || (secType === 'MCQ' ? 1 : 2);
    }

    const questionObj = normalizeMcqQuestionRow({
      id: editingQuestionId || Date.now(),
      section_name: secName,
      question: newQuestion,
      type: secType,
      option_a: newOptions.A,
      option_b: newOptions.B,
      option_c: newOptions.C,
      option_d: newOptions.D,
      correct_answer: secType === 'MCQ' ? correctOption : newCorrectAnswer,
      expected_answer: secType === 'DESCRIPTIVE' ? newCorrectAnswer : '',
      marks: marks,
      pedagogy: pedagogySearch || 'General',
    });

    handleAddManualQuestion(questionObj);

    setIsAddingQuestion(false);
    setEditingQuestionId(null);
    setShowPedagogyDropdown(false);
    setPedagogyDropdownPos(null);
  };

    const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Template');

    // 1. Instruction Row (Merged)
    const instructionStr = QB_CSV_INSTRUCTION.replace(/^# /, '');
    const firstRow = worksheet.getRow(1);
    firstRow.getCell(1).value = instructionStr;
    worksheet.mergeCells('A1:K1');

    const mergedCell = worksheet.getCell('A1');
    mergedCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE599' } };
    mergedCell.font = { bold: true, color: { argb: 'FF1E293B' } };
    mergedCell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
    firstRow.height = 30;

    // 2. Headers
    const headerRow = worksheet.getRow(2);
    QB_CSV_HEADERS.forEach((header, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.value = header;
      cell.font = { bold: true, color: { argb: 'FF1E293B' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // 3. Sample Rows
    const sampleRows = [
      ['General', 'What is 2+2?', 'MCQ', '3', '4', '5', '6', 'B', '1', '', 'Factual'],
      [
        'General',
        'Explain gravity.',
        'Descriptive question',
        '',
        '',
        '',
        '',
        'Force that pulls objects towards each other.',
        '2',
        'Force that pulls objects towards each other.',
        'Conceptual',
      ],
    ];

    sampleRows.forEach((rowValues, rowIndex) => {
      const row = worksheet.getRow(3 + rowIndex);
      rowValues.forEach((val, colIndex) => {
        row.getCell(colIndex + 1).value = val;
      });
    });

    // Adjust column widths
    worksheet.columns = [
      { width: 15 },
      { width: 35 },
      { width: 20 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 10 },
      { width: 35 },
      { width: 20 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'question_template.xlsx');
  };

  const handleAddBulkQuestions = (uploadedQuestions) => {
    if (!uploadedQuestions || uploadedQuestions.length === 0) return;

    let currentSections = [...templateSections];
    let currentPaper = generatedPaper ? [...generatedPaper] : [];

    // If templateSections is empty, we populate sections and paper dynamically based on all uploaded questions (old behavior)
    if (currentSections.length === 0) {
      uploadedQuestions.forEach((q) => {
        const secType = String(q.type || '').toUpperCase() === 'DESCRIPTIVE' ? 'DESCRIPTIVE' : 'MCQ';
        const secName = q.section_name || (secType === 'MCQ' ? 'MCQ Section' : 'Descriptive Section');
        const marks = Number(q.marks) || (secType === 'MCQ' ? 1 : 2);

        // 1. Update currentSections
        const secIdx = currentSections.findIndex(s => s.name.toLowerCase() === secName.toLowerCase());
        if (secIdx > -1) {
          currentSections[secIdx] = {
            ...currentSections[secIdx],
            count: (Number(currentSections[secIdx].count) || 0) + 1
          };
        } else {
          currentSections.push({
            name: secName,
            type: secType,
            marks: marks,
            count: 1
          });
        }

        // 2. Update currentPaper
        let paperSecIdx = currentPaper.findIndex(s => s.sectionName.toLowerCase() === secName.toLowerCase());
        const newQ = {
          ...q,
          question: (q.question || '').replace(/\s*\(Q\d+\)\s*$/gi, '').trim(),
          template_section: secName,
          section_idx: paperSecIdx > -1 ? paperSecIdx : currentPaper.length,
          marks: marks,
          type: secType
        };

        if (paperSecIdx > -1) {
          currentPaper[paperSecIdx] = {
            ...currentPaper[paperSecIdx],
            questions: [...currentPaper[paperSecIdx].questions, newQ]
          };
        } else {
          paperSecIdx = currentPaper.length;
          newQ.section_idx = paperSecIdx;
          currentPaper.push({
            sectionName: secName,
            sectionMarks: marks,
            sectionType: secType,
            questions: [newQ]
          });
        }
      });
      
      setTemplateSections(currentSections);
      setGeneratedPaper(currentPaper);
      toast.success(`${uploadedQuestions.length} questions uploaded successfully!`);
      return;
    }

    // If templateSections is NOT empty, we fill the existing sections up to their defined count.
    // Initialize currentPaper with the existing sections if they are not already in currentPaper
    currentSections.forEach((sec, idx) => {
      const paperSecIdx = currentPaper.findIndex(s => s.sectionName.toLowerCase() === sec.name.toLowerCase());
      if (paperSecIdx === -1) {
        currentPaper.push({
          sectionName: sec.name,
          sectionMarks: sec.marks,
          sectionType: sec.type,
          questions: []
        });
      }
    });

    let importedCount = 0;

    uploadedQuestions.forEach((q) => {
      const secType = String(q.type || '').toUpperCase() === 'DESCRIPTIVE' ? 'DESCRIPTIVE' : 'MCQ';
      const secName = q.section_name || '';

      // Find matching section in currentSections:
      // Match by name first, otherwise match by type.
      let matchedSecIdx = -1;
      if (secName) {
        matchedSecIdx = currentSections.findIndex(s => s.name.toLowerCase() === secName.toLowerCase());
      }
      if (matchedSecIdx === -1) {
        matchedSecIdx = currentSections.findIndex(s => s.type === secType);
      }

      if (matchedSecIdx > -1) {
        const matchedSec = currentSections[matchedSecIdx];
        const paperSecIdx = currentPaper.findIndex(s => s.sectionName.toLowerCase() === matchedSec.name.toLowerCase());
        
        if (paperSecIdx > -1) {
          const currentQuestionsInSec = currentPaper[paperSecIdx].questions || [];
          const targetLimit = Number(matchedSec.count) || 0;

          if (currentQuestionsInSec.length < targetLimit) {
            const newQ = {
              ...q,
              question: (q.question || '').replace(/\s*\(Q\d+\)\s*$/gi, '').trim(),
              template_section: matchedSec.name,
              section_idx: paperSecIdx,
              marks: Number(matchedSec.marks) || 1,
              type: matchedSec.type
            };
            currentPaper[paperSecIdx].questions = [...currentQuestionsInSec, newQ];
            importedCount++;
          }
        }
      }
    });

    setGeneratedPaper(currentPaper);
    if (importedCount > 0) {
      toast.success(`${importedCount} questions uploaded matching your template structure limits!`);
    } else {
      toast.error("No questions could be imported because the template structure limits were already reached or did not match.");
    }
  };

  const handleBulkUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = isExcel
        ? parseQuestionBankExcel(e.target.result)
        : parseQuestionBankCsv(e.target.result);

      if (!result.success) {
        toast.error(result.error);
        return;
      }

      handleAddBulkQuestions(result.questions);
    };

    if (isExcel) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }

    event.target.value = null;
  };

  const handleTemplateClick = () => {
    if (selectedBank) {
      if (templateSections.length === 0) {
        setIsAddingSection(true);
        toast.info("Please add at least one section to the template structure.");
      } else {
        handleGeneratePaper();
      }
    } else {
      setIsAddingSection(true);
      toast.info("Please add a template section or select a Question Bank first.");
    }
  };

  const resetToNewTemplate = () => {
    setIsReadOnly(false);
    setEditingTemplateId(null);
    setTemplateName('');
    setSelectedBank('');
    setPassMark('');
    setTemplateSections([]);
    setGeneratedPaper(null);
    setActivePreviewSection(0);
    setEditingPaperQuestionIdx(null);
    setEditingSectionIdx(null);
    setIsAddingSection(true);
    setNewSection({ ...defaultNewSection });
    resetModalState();
    templateScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasTemplateDraft =
    Boolean(editingTemplateId) ||
    Boolean(templateName.trim()) ||
    Boolean(selectedBank) ||
    templateSections.length > 0 ||
    Boolean(generatedPaper);

  const totalQuestions = templateSections.reduce((acc, s) => acc + (Number(s.count) || 0), 0);
  const totalMarks = templateSections.reduce((acc, s) => acc + ((Number(s.count) || 0) * (Number(s.marks) || 0)), 0);

  const activeSectionsList = useMemo(() => {
    if (generatedPaper && generatedPaper.length > 0) {
      return generatedPaper;
    }
    return templateSections.map((sec) => ({
      sectionName: sec.name,
      sectionMarks: sec.marks,
      sectionType: sec.type,
      questions: [],
    }));
  }, [generatedPaper, templateSections]);

  const actualQuestionsCount = useMemo(() => {
    if (!generatedPaper) return 0;
    return generatedPaper.reduce((acc, sec) => acc + (sec.questions?.length || 0), 0);
  }, [generatedPaper]);

  const handleGeneratePaper = () => {
    if (!selectedBank) return toast.error("Please select a Question Bank first.");
    if (templateSections.length === 0) return toast.error("Please add at least one section to the structure.");

    const bank = banks.find(b => b.name === selectedBank);
    if (!bank || !bank.questions) return toast.error("Selected bank has no questions.");
    
    const bankPool = bank.questions;
    
    let paper = [];
    templateSections.forEach((section, idx) => {
      const pool = bankPool.filter(q => (q.type || '').toUpperCase() === (section.type || '').toUpperCase());
      if (pool.length < section.count) {
        toast.error(`Not enough ${section.type} questions in the bank "${selectedBank}". Need ${section.count}, but only found ${pool.length}.`);
        return;
      }
      
      const shuffled = [...pool].sort(() => 0.5 - Math.random());
      const selected = shuffled
        .slice(0, section.count)
        .map((q) => normalizeQuestionForPaper(q, section, idx));
      
      paper.push({
        sectionName: section.name,
        sectionMarks: section.marks,
        sectionType: section.type,
        questions: selected
      });
    });

    setGeneratedPaper(paper);
    setActivePreviewSection(0);
  };

  const handleReshuffle = () => {
    if (!generatedPaper) return;
    const reshuffled = generatedPaper.map(section => ({
      ...section,
      questions: [...section.questions].sort(() => 0.5 - Math.random())
    }));
    setGeneratedPaper(reshuffled);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return toast.error("Please enter a Template Name.");
    if (templateSections.length === 0) return toast.error("Please add at least one section.");

    setLoading(true);
    try {
      const payload = {
        id: editingTemplateId,
        name: templateName,
        passMark: passMark === '' ? 0 : passMark,
        totalQuestions,
        totalMarks,
        bank: selectedBank,
        sections: templateSections,
        questions: generatedPaper ? generatedPaper.flatMap(s => s.questions) : []
      };
      const res = await testService.saveTemplate(payload);
      if (res.status === 'success') {
        toast.success(editingTemplateId ? "Template updated successfully!" : "Template saved successfully!");
        setEditingTemplateId(res.id);
        fetchData();
      } else {
        toast.error(res.message || "Failed to save template");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving template");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaperQuestion = (secIdx, qIdx, updatedQ) => {
    const updated = [...generatedPaper];
    updated[secIdx].questions[qIdx] = updatedQ;
    setGeneratedPaper(updated);
    setEditingPaperQuestionIdx(null);
  };

  const handleDeletePaperQuestion = (secIdx, qIdx) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete Question',
      message: 'Are you sure you want to remove this question from the template?',
      confirmText: 'Delete',
      onConfirm: () => {
        // 1. Remove the question from generatedPaper
        let updatedPaper = null;
        if (generatedPaper) {
          updatedPaper = generatedPaper.map((sec, sIdx) => {
            if (sIdx === secIdx) {
              return {
                ...sec,
                questions: sec.questions.filter((_, qI) => qI !== qIdx)
              };
            }
            return sec;
          });
          setGeneratedPaper(updatedPaper);
        }

        // 2. Decrement corresponding section count in templateSections
        setTemplateSections((prev) => {
          const next = [...prev];
          if (next[secIdx]) {
            const currentCount = Number(next[secIdx].count) || 0;
            next[secIdx] = {
              ...next[secIdx],
              count: Math.max(0, currentCount - 1)
            };
          }
          return next;
        });

        setPopupConfig({ isOpen: false });
        toast.success('Question deleted successfully');
      }
    });
  };

  const handleDeleteSection = (idx) => {
    const section = templateSections[idx];
    const sectionLabel =
      section?.name || (section?.type === 'MCQ' ? 'MCQ Section' : 'Descriptive Section');

    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this section?',
      message: `Are you sure you want to remove "${sectionLabel}" from the template structure?`,
      confirmText: 'Yes, delete it!',
      onConfirm: () => {
        setTemplateSections((prev) => {
          const next = prev.filter((_, i) => i !== idx);
          setIsAddingSection(next.length === 0);
          return next;
        });
        if (editingSectionIdx === idx) {
          setEditingSectionIdx(null);
        } else if (editingSectionIdx != null && editingSectionIdx > idx) {
          setEditingSectionIdx(editingSectionIdx - 1);
        }
        setGeneratedPaper(null);
        setPopupConfig({ isOpen: false });
        toast.success('Section deleted');
      },
    });
  };

  const handleEditTemplate = (template, readOnly = false) => {
    setIsReadOnly(readOnly);
    setEditingTemplateId(template.id);
    setTemplateName(template.name);
    setSelectedBank(template.bank);
    const pm = template.pass_mark ?? template.passMark;
    setPassMark(pm === 0 || pm === undefined ? '' : pm);
    setActivePreviewSection(0);
    setEditingPaperQuestionIdx(null);
    setIsAddingSection(false);
    
    const mappedSections = (template.sections || []).map(sec => ({
      name: sec.section_name || sec.name,
      type: sec.marks_type || sec.type,
      count: sec.num_questions || sec.count,
      marks: sec.marks_per_question || sec.marks
    }));
    setTemplateSections(mappedSections);
    
    if (template.questions && template.questions.length > 0) {
       const grouped = [];
       mappedSections.forEach((sec, sIdx) => {
          const secQuestions = (template.questions || []).filter(q => 
            (q.template_section === sec.name) || 
            (q.section_idx !== undefined && Number(q.section_idx) === sIdx)
          );
          grouped.push({
            sectionName: sec.name,
            sectionMarks: sec.marks,
            sectionType: sec.type,
            questions: secQuestions.map((q) => normalizeQuestionForPaper(q, sec, sIdx)),
          });
       });
       setGeneratedPaper(grouped);
    } else {
       setGeneratedPaper(null);
    }
  };

  const handleCopyTemplate = (template) => {
    setIsReadOnly(false);
    setEditingTemplateId(null);
    setTemplateName(`${template.name} (Copy)`);
    setSelectedBank('');
    const pm2 = template.pass_mark ?? template.passMark;
    setPassMark(pm2 === 0 || pm2 === undefined ? '' : pm2);
    
    const mappedSections = (template.sections || []).map(sec => ({
      name: sec.section_name || sec.name,
      type: sec.marks_type || sec.type,
      count: sec.num_questions || sec.count,
      marks: sec.marks_per_question || sec.marks
    }));
    setTemplateSections(mappedSections);
    setGeneratedPaper(null);
    setIsAddingSection(false);
  };

  const handleDeleteTemplate = (id) => {
    setPopupConfig({
      isOpen: true,
      type: 'warning',
      title: 'Delete this template?',
      message: 'Are you sure you want to delete this template? This action cannot be undone.',
      confirmText: 'Yes, delete it!',
      onConfirm: async () => {
        try {
          const res = await testService.deleteTemplate(id);
          if (res.status === 'success') {
            setPopupConfig({ isOpen: false });
            if (String(editingTemplateId) === String(id)) {
              resetToNewTemplate();
            }
            fetchData();
            toast.success('Template deleted successfully');
          } else {
            toast.error(res.message || 'Failed to delete template');
            setPopupConfig({ isOpen: false });
          }
        } catch (err) {
          console.error(err);
          setPopupConfig({ isOpen: false });
          toast.error('Failed to delete template');
        }
      }
    });
  };

  return (
    <div className="assess-mod-page assess-mod-template-page flex flex-col h-full min-h-0 bg-[#f8fafc] overflow-hidden font-sans">
      {/* Top Header */}
      <header className="assess-mod-header h-[70px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="assess-mod-back-mobile lg:hidden"
          aria-label="Go back"
        >
          <FiArrowLeft className="text-lg" />
        </button>
        <div className="assess-mod-header-brand flex items-center gap-4">
          <div className="w-10 h-10 bg-[#b91c1c] rounded-xl flex items-center justify-center shadow-lg shadow-rose-200">
            <FiZap className="text-white text-xl fill-current" />
          </div>
          <div>
            <h1 className="text-[16px] font-black text-[#1e293b] tracking-tight leading-none">
              {isReadOnly
                ? 'View Template'
                : editingTemplateId
                  ? 'Edit Template'
                  : 'Create New Template'}
            </h1>
          </div>
        </div>
        <div className="assess-mod-header-actions flex items-center gap-3">
          {(editingTemplateId || hasTemplateDraft) && (
            <button
              type="button"
              onClick={resetToNewTemplate}
              className="btn-assessment btn-assessment-danger"
            >
              <FiPlus className="text-sm" /> Create New Template
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="btn-assessment btn-assessment-danger hidden lg:inline-flex"
          >
            <FiArrowLeft className="text-sm" /> Back
          </button>
        </div>
      </header>

      <div className="assess-mod-body flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <aside className="assess-mod-sidebar w-full lg:w-[300px] bg-white border-e border-slate-200 flex flex-col shrink-0 overflow-hidden">
          <div className="assess-mod-sidebar-search p-6 shrink-0 border-b border-slate-100">
            <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <input 
                  type="text"
                  placeholder="Search templates..."
                  className="w-full bg-[#f1f5f9] border border-slate-200 rounded-xl pl-10 pr-4 py-2 text-[12px] font-bold focus:ring-4 focus:ring-rose-50 transition-all placeholder:text-slate-400 shadow-inner"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
          </div>
          <div className="assess-mod-template-list assess-mod-sidebar-list flex-1 min-h-0 overflow-y-auto p-6 flex flex-col items-center gap-3 custom-scrollbar">
                {savedTemplates.filter(t => t.name.toLowerCase().includes(searchTerm.toLowerCase())).map(t => (
                  <div 
                    key={t.id} 
                    onClick={() => handleEditTemplate(t, true)}
                    className={`assess-mod-template-item w-full max-w-[252px] p-3 rounded-[24px] border border-rose-100 transition-all cursor-pointer group flex items-center gap-2.5 ${editingTemplateId === t.id ? 'bg-rose-50 shadow-sm' : 'bg-white hover:border-rose-200'}`}
                  >
                    <div className="assess-mod-template-icon w-9 h-9 rounded-xl flex items-center justify-center text-[#b91c1c] shrink-0">
                      <FiFileText className="text-base" />
                    </div>
                    <div className="flex-1 min-w-0 text-center">
                      <h5 className={`text-[12px] font-black mb-0 tracking-tight truncate ${editingTemplateId === t.id ? 'text-[#b91c1c]' : 'text-[#1e293b]'}`}>
                        {t.name}
                      </h5>
                    </div>
                    <div
                      className="assess-mod-sidebar-actions assess-mod-template-actions flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={e => e.stopPropagation()}
                    >
                      <button 
                        onClick={() => handleCopyTemplate(t)}
                        className="w-7 h-7 bg-emerald-50 text-emerald-500 rounded-lg flex items-center justify-center border border-emerald-100 hover:bg-emerald-500 hover:text-white transition-all"
                        title="Copy Template"
                      >
                        <FiLayers className="text-[10px]" />
                      </button>
                      <button 
                        onClick={() => handleEditTemplate(t, false)}
                        className="w-7 h-7 bg-blue-50 text-blue-500 rounded-lg flex items-center justify-center border border-blue-100 hover:bg-blue-500 hover:text-white transition-all"
                        title="Edit Template"
                      >
                        <AssessmentEditIcon className="w-2.5 h-2.5" />
                      </button>
                      <button 
                        onClick={() => handleDeleteTemplate(t.id)}
                        className="w-7 h-7 bg-rose-50 text-rose-500 rounded-lg flex items-center justify-center border border-rose-100 hover:bg-[#b91c1c] hover:text-white transition-all"
                        title="Delete Template"
                      >
                        <FiTrash2 className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                ))}
                {savedTemplates.length === 0 && (
                  <div className="py-10 text-center">
                    <p className="text-[9px] text-slate-300 font-black uppercase tracking-widest">No templates yet</p>
                  </div>
                )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="assess-mod-main-wrap assess-mod-template-main flex-1 min-w-0 min-h-0 overflow-hidden bg-[#f8fafc] flex flex-col">
          <div
            ref={templateScrollRef}
            className="assess-mod-card-scroll assess-mod-template-scroll flex-1 min-h-0 overflow-y-auto custom-scrollbar w-full"
          >
            
            {/* Top Setup Card */}
            <div className="assess-mod-template-setup-card bg-white rounded-[24px] border border-slate-100 shadow-xl shadow-slate-200/40 p-6">
              <div className="assess-mod-setup-grid grid grid-cols-4 gap-6 items-start">
                
                {/* Template Name */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-rose-50 text-[#b91c1c] rounded-xl flex items-center justify-center border border-rose-100 shadow-sm">
                      <FiFileText className="text-lg" />
                    </div>
                    <div>
                      <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5">TEMPLATE NAME</h6>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">DEFINE TEMPLATE NAME</p>
                    </div>
                  </div>
                  <input 
                    type="text"
                    placeholder="e.g. Technical Skills 2024"
                    className="w-full bg-[#f8fafc] border border-slate-100 rounded-xl px-5 py-2.5 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-rose-50 outline-none transition-all placeholder:text-slate-300 shadow-inner disabled:opacity-50"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>

                {/* Select Question Bank */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm shrink-0">
                      <FiGrid className="text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0">SELECT QUESTION BANK</h6>
                        <button
                          type="button"
                          onClick={() => navigateToModule('questionBank', 'templates')}
                          className="shrink-0 p-1.5 bg-[#b91c1c] text-white rounded-full flex items-center justify-center hover:bg-[#991b1b] transition-colors leading-none"
                          title="Add question bank"
                          aria-label="Add question bank"
                        >
                          <FiPlus className="text-[10px] stroke-[2.5]" />
                        </button>
                      </div>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">CHOOSE REPOSITORY</p>
                    </div>
                  </div>
                  <div className="relative">
                    <select 
                      className="w-full bg-[#f8fafc] border border-slate-100 rounded-xl px-5 py-2.5 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-rose-50 outline-none transition-all appearance-none shadow-inner disabled:opacity-50"
                      value={selectedBank}
                      onChange={(e) => {
                          setSelectedBank(e.target.value);
                          setGeneratedPaper(null);
                      }}
                      disabled={isReadOnly}
                      onFocus={async () => {
                        try {
                          const res = await testService.getQuestionBanks();
                          if (res.status === 'success') setBanks(res.banks);
                        } catch (err) {
                          console.error("Failed to refresh banks:", err);
                        }
                      }}
                    >
                      <option value="">-- Select a Question Bank --</option>
                      {banks.map(bank => (
                        <option key={bank.id} value={bank.name}>{bank.name}</option>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Pass Mark */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm">
                      <FiPercent className="text-lg" />
                    </div>
                    <div>
                      <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5">PASS MARK</h6>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">MINIMUM SCORE TO PASS</p>
                    </div>
                  </div>
                   <input 
                    type="number"
                    placeholder="0"
                    className="w-full bg-[#f8fafc] border border-slate-100 rounded-2xl px-5 py-3 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-rose-50 outline-none transition-all shadow-inner disabled:opacity-50"
                    value={passMark}
                    onChange={(e) => setPassMark(e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>

                {/* Totals */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-slate-50 text-slate-600 rounded-xl flex items-center justify-center border border-slate-100 shadow-sm">
                      <FiBarChart2 className="text-lg" />
                    </div>
                    <div>
                      <h6 className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5">TOTALS</h6>
                      <p className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-none">FROM STRUCTURE</p>
                    </div>
                  </div>
                  <div className="flex items-center bg-[#f8fafc] rounded-xl border border-slate-100 overflow-hidden shadow-inner h-[42px]">
                    <div className="flex-1 text-center border-e border-slate-200">
                      <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">QUESTIONS</span>
                      <span className="text-[16px] font-black text-[#1e293b] leading-none">{totalQuestions}</span>
                    </div>
                    <div className="flex-1 text-center">
                      <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5">MARKS</span>
                      <span className="text-[16px] font-black text-[#b91c1c] leading-none">{totalMarks}</span>
                    </div>
                  </div>
                </div>
              </div>

                <div className="mt-8 bg-[#f8fafc] rounded-[24px] border border-slate-100 overflow-hidden assess-mod-section-table-wrap">
                  {/* Table Header */}
                  <div className="assess-mod-section-table-head px-8 py-3 grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center bg-white border-b border-slate-100">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em]">SECTION / TYPE</span>
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] text-center">QUESTIONS</span>
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] text-center">MARKS EACH</span>
                    <div className="flex items-center justify-end gap-3">
                      <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">ACTIONS</span>
                      {!isReadOnly && templateSections.length < 2 && (
                        <button 
                          onClick={() => setIsAddingSection(true)}
                          className="w-8 h-8 bg-[#b91c1c] text-white rounded-full flex items-center justify-center hover:bg-[#991b1b] transition-all shadow-md shadow-red-100"
                        >
                          <FiPlus className="text-sm" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Existing Sections */}
                  {templateSections.map((section, idx) => (
                    <div key={idx} className="border-b border-slate-50 last:border-0">
                      {editingSectionIdx === idx ? (
                        <div className="assess-mod-section-table-row px-8 py-4 bg-rose-50/20 grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center animate-in fade-in duration-300">
                          <div className="flex-1">
                            <select 
                              className="w-full bg-white border border-rose-100 rounded-xl px-4 py-2 text-[12px] font-black text-[#1e293b] outline-none shadow-sm"
                              value={section.type}
                              onChange={(e) => {
                                const updated = [...templateSections];
                                updated[idx] = { ...section, type: e.target.value, name: e.target.value === 'MCQ' ? 'MCQ Section' : 'Descriptive Section' };
                                setTemplateSections(updated);
                              }}
                            >
                              <option value="MCQ">MCQ Section</option>
                              <option value="DESCRIPTIVE">Descriptive Section</option>
                            </select>
                          </div>
                          <div className="w-[120px]">
                            <input 
                              type="number" 
                              className="w-full bg-white border border-rose-100 rounded-xl px-2 py-2 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm" 
                              value={section.count}
                              onChange={(e) => {
                                const updated = [...templateSections];
                                updated[idx] = { ...section, count: e.target.value };
                                setTemplateSections(updated);
                              }}
                            />
                          </div>
                          <div className="w-[120px]">
                            <input 
                              type="number" 
                              className="w-full bg-white border border-rose-100 rounded-xl px-2 py-2 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm" 
                              value={section.marks}
                              onChange={(e) => {
                                const updated = [...templateSections];
                                updated[idx] = { ...section, marks: e.target.value };
                                setTemplateSections(updated);
                              }}
                            />
                          </div>
                          <div className="w-[160px] flex items-center justify-end gap-2">
                            <AssessmentConfirmIconButton
                              onClick={() => setEditingSectionIdx(null)}
                              title="Update section"
                            />
                            <AssessmentCancelIconButton
                              onClick={() => setEditingSectionIdx(null)}
                              title="Cancel"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="assess-mod-section-table-row px-8 py-4 bg-white grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center hover:bg-slate-50/30 transition-all group">
                          <div className="flex-1">
                            <div className="text-[12px] font-black text-[#1e293b]">{section.name || (section.type === 'MCQ' ? 'MCQ Section' : 'Descriptive Section')}</div>
                            <div className="text-[8px] text-slate-400 font-black uppercase tracking-[0.1em] mt-0.5">
                              {section.type === 'MCQ' ? 'MCQ COMPONENT' : 'DESCRIPTIVE QUESTION COMPONENT'}
                            </div>
                          </div>
                          <div className="w-[120px] text-center text-[12px] font-black text-[#1e293b]">{section.count}</div>
                          <div className="w-[120px] text-center text-[12px] font-black text-[#1e293b]">{section.marks}</div>
                          <div className="w-[140px] flex items-center justify-end gap-3 transition-all">
                            {!isReadOnly && (
                              <>
                                <AssessmentEditIconButton
                                  onClick={() => setEditingSectionIdx(idx)}
                                  title="Edit section"
                                />
                                <AssessmentDeleteIconButton
                                  onClick={() => handleDeleteSection(idx)}
                                  title="Delete section"
                                />
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Input Row */}
                  {isAddingSection && editingSectionIdx === null && (
                    <div className="assess-mod-section-table-input p-4 bg-slate-50/50 m-2 rounded-[24px] border-2 border-dashed border-slate-200 grid grid-cols-[1fr_120px_120px_180px] gap-6 items-center animate-in slide-in-from-top-2 duration-300">
                      <div className="flex-1 px-4">
                        <div className="relative">
                          <select 
                            className="w-full bg-white border border-slate-200 rounded-xl px-5 py-2.5 text-[12px] font-black text-[#1e293b] outline-none appearance-none shadow-sm"
                            value={newSection.type}
                            onChange={(e) => setNewSection({...newSection, type: e.target.value, name: e.target.value === 'MCQ' ? 'MCQ Section' : 'Descriptive Section'})}
                          >
                            <option value="">Choose Type...</option>
                            {!templateSections.some(s => s.type === 'MCQ') && <option value="MCQ">MCQ Section</option>}
                            {!templateSections.some(s => s.type === 'DESCRIPTIVE') && <option value="DESCRIPTIVE">Descriptive Section</option>}
                          </select>
                          <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        </div>
                      </div>
                      <div className="w-[120px] px-2">
                        <input 
                          type="number" 
                          placeholder="0"
                          className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2.5 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm" 
                          value={newSection.count}
                          onChange={(e) => setNewSection({...newSection, count: e.target.value})}
                        />
                      </div>
                      <div className="w-[120px] px-2">
                        <input 
                          type="number" 
                          placeholder="0"
                          className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2.5 text-[12px] font-black text-[#1e293b] text-center outline-none shadow-sm" 
                          value={newSection.marks}
                          onChange={(e) => setNewSection({...newSection, marks: e.target.value})}
                        />
                      </div>
                      <div className="w-[180px] px-4 flex items-center justify-end gap-2">
                        <AssessmentConfirmIconButton
                          onClick={() => {
                            if (!newSection.type) return toast.error("Select section type");
                            if (templateSections.length >= 2) return toast.error("Maximum 2 sections allowed");
                            const finalSection = {
                              ...newSection,
                              name: newSection.type === 'MCQ' ? 'MCQ Section' : 'Descriptive Section',
                            };
                            setTemplateSections([...templateSections, finalSection]);
                            setNewSection({ name: '', type: 'MCQ', marks: 1, count: 10 });
                            setIsAddingSection(false);
                            setEditingSectionIdx(null);
                            setGeneratedPaper(null);
                          }}
                          title="Add section"
                        />
                        <AssessmentCancelIconButton
                          onClick={() => setIsAddingSection(false)}
                          title="Cancel"
                        />
                      </div>
                    </div>
                  )}

                  {!isAddingSection && templateSections.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 opacity-10">
                      <FiLayout className="text-6xl mb-4" />
                      <p className="text-[12px] font-black uppercase tracking-widest">No Sections Added</p>
                    </div>
                  )}
                </div>

                {/* Final Actions */}
                {templateSections.length > 0 && (
                  <div className="assess-mod-actions-row mt-8 flex justify-end gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {!selectedBank ? (
                      <>
                        <button 
                          type="button"
                          onClick={handleDownloadTemplate}
                          className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6"
                        >
                          <FiDownload className="text-sm" /> Template
                        </button>
                        <button 
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6"
                        >
                          <FiCloud className="text-sm" /> Bulk Upload
                        </button>
                        <button 
                          type="button"
                          onClick={handleAddQuestion}
                          className="btn-assessment btn-assessment-danger !py-2 !px-6"
                        >
                          <FiPlus className="text-sm" /> Add Question
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          type="button"
                          onClick={handleGeneratePaper}
                          className="btn-assessment btn-assessment-danger"
                        >
                          <FiSettings className="text-sm" />
                          QP Generation
                        </button>
                        <button 
                          type="button"
                          onClick={handleReshuffle}
                          className="btn-assessment btn-assessment-danger"
                        >
                          <FiRefreshCcw className="text-sm" />
                          Re-Shuffle
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Paper Preview Section */}
              {activeSectionsList.length > 0 && (
                <div className="assess-mod-paper-preview assess-mod-table-wrap bg-white rounded-[32px] border border-slate-100 shadow-2xl animate-in fade-in zoom-in-95 duration-500">
                  <div className="assess-mod-paper-preview-head flex items-center justify-between border-b border-slate-50">
                    <div className="assess-mod-paper-preview-head-inner flex items-center">
                      <div>
                        <h4 className="text-[15px] font-black text-[#1e293b] mb-0.5 uppercase tracking-tight">Question Paper Preview</h4>
                        <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest leading-none">LIVE GENERATED DRAFT</p>
                      </div>
                      
                      {/* Navigation Arrows */}
                      <div className="assess-mod-paper-preview-nav flex items-center bg-slate-50 rounded-xl border border-slate-100">
                        <button 
                          onClick={() => setActivePreviewSection(prev => Math.max(0, prev - 1))}
                          disabled={activePreviewSection === 0}
                          className={`assess-mod-paper-preview-nav-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${activePreviewSection === 0 ? 'text-slate-300' : 'bg-white text-[#b91c1c] shadow-sm hover:bg-rose-50'}`}
                        >
                          <FiChevronLeft className="text-xl" />
                        </button>
                        <div className="px-2.5 text-[10px] font-black text-[#1e293b] uppercase tracking-widest">
                          Section {activePreviewSection + 1} <span className="text-slate-400 mx-1">/</span> {activeSectionsList.length}
                        </div>
                        <button 
                          onClick={() => setActivePreviewSection(prev => Math.min(activeSectionsList.length - 1, prev + 1))}
                          disabled={activePreviewSection === activeSectionsList.length - 1}
                          className={`assess-mod-paper-preview-nav-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${activePreviewSection === activeSectionsList.length - 1 ? 'text-slate-300' : 'bg-white text-[#b91c1c] shadow-sm hover:bg-rose-50'}`}
                        >
                          <FiChevronRight className="text-xl" />
                        </button>
                      </div>
                    </div>
                    <div className="assess-mod-paper-preview-stats flex">
                      <div className="assess-mod-paper-preview-stat bg-slate-50 rounded-lg border border-slate-100 flex items-center">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">TOTAL QUESTIONS</span>
                        <span className="text-[13px] font-black text-[#1e293b]">{activeSectionsList.reduce((acc, s) => acc + (s.questions?.length || 0), 0)}</span>
                      </div>
                      <div className="assess-mod-paper-preview-stat bg-rose-50 rounded-lg border border-rose-100 flex items-center">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest text-[#b91c1c]">TOTAL MARKS</span>
                        <span className="text-[13px] font-black text-[#b91c1c]">
                          {activeSectionsList.reduce((acc, s) => acc + ((s.questions?.length || 0) * (Number(s.sectionMarks) || 0)), 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="assess-mod-paper-preview-body">
                    {activeSectionsList[activePreviewSection] && (
                      <div className="assess-mod-paper-preview-section animate-in slide-in-from-right-4 duration-500">
                        <div className="assess-mod-paper-preview-section-head flex items-center">
                          <span className="assess-mod-paper-preview-section-badge bg-[#1e293b] text-white text-[9px] font-black uppercase tracking-widest rounded-full shadow-lg">
                            {activeSectionsList[activePreviewSection].sectionName}
                          </span>
                          <div className="h-px flex-1 bg-slate-50"></div>
                        </div>
                        
                        <div className="assess-mod-paper-preview-questions">
                          {/* Header Labels */}
                          <div className="assess-mod-paper-preview-grid-head grid gap-6 bg-white border border-slate-100 rounded-full shadow-sm items-center hidden lg:grid grid-cols-[60px_180px_minmax(0,1fr)_340px] px-6">
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">#</div>
                            <div className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest">PEDAGOGY</div>
                            <div className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest">QUESTION CONTENT</div>
                            <div className="text-[10px] font-black text-[#1e293b] uppercase tracking-widest">
                              {activeSectionsList[activePreviewSection].sectionType === 'MCQ' ? 'OPTION DETAILS' : 'EXPECTED ANSWER'}
                            </div>
                          </div>

                          {/* Inline Question Input Row */}
                          {isAddingQuestion && (
                            <div className="assess-mod-add-question mb-6 shrink-0 w-full min-w-0 mt-3">
                              <div className="bg-white border border-red-100 rounded-[24px] p-4 sm:p-6 shadow-xl shadow-red-500/5 animate-in fade-in slide-in-from-top-4 duration-300">
                                <div className="assess-mod-add-question-fields lg:overflow-x-auto lg:custom-scrollbar lg:pb-1">
                                  <div className="assess-mod-add-question-grid flex flex-col gap-4 w-full min-w-0 lg:grid lg:gap-6 lg:items-start lg:grid-cols-[60px_180px_minmax(0,1fr)_340px] px-6">
                                    {/* Col 1: Index */}
                                    <div className="flex flex-col items-center max-lg:flex-row max-lg:items-center max-lg:gap-3 max-lg:w-full max-lg:pb-3 max-lg:border-b max-lg:border-slate-100">
                                      <div className="w-10 h-10 bg-[#1e293b] text-white rounded-full flex items-center justify-center font-black text-[14px] shadow-lg border-4 border-white shrink-0">
                                        {activeSectionsList[activePreviewSection].questions.length + 1}
                                      </div>
                                      <div className="max-lg:mt-0 mt-2 text-[8px] font-black px-2 py-0.5 rounded border uppercase tracking-widest bg-red-50 text-[#b91c1c] border-red-100">
                                        {activeSectionsList[activePreviewSection].sectionType === 'MCQ' ? 'MCQ' : 'DESC'}
                                      </div>
                                    </div>

                                    {/* Col 2: Pedagogy */}
                                    <div className="relative" ref={pedagogyRef}>
                                      <PedagogyFieldHeader
                                        deleteMode={pedagogyDeleteMode}
                                        onToggleDeleteMode={togglePedagogyDeleteMode}
                                      />
                                      <div className="relative cursor-pointer" ref={pedagogyAnchorRef}>
                                        <input 
                                          type="text"
                                          placeholder="Search or type pedagogy..."
                                          className={`w-full bg-[#f8fafc] border rounded-lg px-3 pr-8 py-2 text-sm font-bold text-[#1e293b] outline-none transition-all placeholder:text-slate-300 cursor-pointer ${showPedagogyDropdown ? 'border-red-600 ring-2 ring-red-50' : 'border-slate-100'}`}
                                          value={pedagogySearch}
                                          onChange={(e) => {
                                            setPedagogySearch(e.target.value);
                                            if (!showPedagogyDropdown) {
                                              setShowPedagogyDropdown(true);
                                              requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                            }
                                          }}
                                          onFocus={() => {
                                            setShowPedagogyDropdown(true);
                                            requestAnimationFrame(() => syncPedagogyDropdownPosition());
                                          }}
                                          onClick={togglePedagogyDropdown}
                                        />
                                        <FiChevronDown 
                                          className={`absolute right-3 top-1/2 -translate-y-1/2 text-[12px] transition-all pointer-events-none ${showPedagogyDropdown ? 'text-[#b91c1c]' : 'text-slate-400'}`} 
                                        />
                                      </div>

                                      {showPedagogyDropdown && pedagogyDropdownPos && typeof document !== 'undefined' && createPortal(
                                        <PedagogyDropdownMenu
                                          menuRef={pedagogyMenuRef}
                                          position={pedagogyDropdownPos}
                                          filteredOptions={filteredPedagogyOptions}
                                          {...pedagogyDropdownMenuProps}
                                        />,
                                        document.body
                                      )}
                                    </div>

                                    {/* Col 3: Question Content */}
                                    <div className="flex flex-col h-full">
                                      <div className="mb-1.5">
                                        <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-0.5 max-lg:visible lg:invisible">QUESTION CONTENT</h6>
                                      </div>
                                      <textarea 
                                        placeholder="Question..."
                                        rows="2"
                                        className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none leading-relaxed min-h-[90px]"
                                        value={newQuestion}
                                        onChange={(e) => setNewQuestion(e.target.value)}
                                      />
                                    </div>

                                    {/* Col 4: Options / Expected Answer */}
                                    {activeSectionsList[activePreviewSection].sectionType === 'MCQ' ? (
                                      <div className="w-full min-w-0">
                                        <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-1.5 lg:hidden">OPTION DETAILS</h6>
                                        <div className="grid grid-cols-2 gap-2">
                                          {['A', 'B', 'C', 'D'].map(opt => (
                                            <div key={opt} className="relative">
                                              <input 
                                                type="text"
                                                placeholder={`${opt} ...`}
                                                className={`w-full bg-white border rounded-lg pl-8 pr-8 py-2 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 ${correctOption === opt ? 'border-red-400 ring-2 ring-red-50' : 'border-slate-100'}`}
                                                value={newOptions[opt]}
                                                onChange={(e) => setNewOptions({...newOptions, [opt]: e.target.value})}
                                              />
                                              <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-black transition-all ${correctOption === opt ? 'text-[#b91c1c]' : 'text-slate-300'}`}>{opt}</span>
                                              <div 
                                                onClick={() => setCorrectOption(opt)}
                                                className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${correctOption === opt ? 'border-red-600 bg-[#b91c1c]' : 'border-slate-200 bg-white hover:border-red-400'}`}
                                              >
                                                {correctOption === opt && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="w-full min-w-0">
                                        <h6 className="text-[8px] font-black text-[#1e293b] uppercase tracking-widest mb-1.5 lg:hidden">EXPECTED ANSWER</h6>
                                        <textarea 
                                          placeholder="Expected Answer..."
                                          className="w-full bg-[#f8fafc] border border-slate-100 rounded-lg px-4 py-2.5 text-sm font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300 resize-none min-h-[90px] leading-relaxed"
                                          value={newCorrectAnswer}
                                          onChange={(e) => setNewCorrectAnswer(e.target.value)}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="assess-mod-add-question-actions flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-5 pt-4 border-t border-slate-100 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => setIsAddingQuestion(false)}
                                    className="btn-assessment w-full sm:w-auto bg-white !text-slate-600 border border-slate-200 !shadow-none hover:!bg-slate-50 hover:!text-slate-800"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleManualSave}
                                    className="btn-assessment btn-assessment-danger w-full sm:w-auto"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          {activeSectionsList[activePreviewSection].questions.length === 0 && !isAddingQuestion && (
                            <div className="flex flex-col items-center justify-center py-16 text-slate-400 bg-[#f8fafc] border border-dashed border-slate-200 rounded-[20px] m-4">
                              <FiLayers className="text-5xl mb-3 opacity-30 text-[#b91c1c]" />
                              <p className="text-[12px] font-black uppercase tracking-widest text-slate-600 mb-1">No questions available in this section.</p>
                              <p className="text-[10px] text-slate-400 font-bold px-6 text-center leading-normal">
                                Click "Add Question" to manually write questions inline, or upload them via "Bulk Upload".
                              </p>
                            </div>
                          )}

                          {activeSectionsList[activePreviewSection].questions.map((q, qIdx) => (
                            <div key={q.id || qIdx} className={`assess-mod-paper-preview-question bg-white border rounded-[20px] shadow-sm transition-all duration-300 group ${editingPaperQuestionIdx === qIdx ? 'border-red-200 shadow-xl ring-1 ring-red-100' : 'border-slate-100 hover:border-red-100 hover:shadow-md'}`}>
                              <div className="assess-mod-question-row grid gap-6 items-start grid-cols-[60px_180px_minmax(0,1fr)_340px] px-6 py-4">
                                
                                {/* Col 1: Index */}
                                <div className="flex flex-col items-center self-center">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-[12px] shadow-sm border transition-colors ${editingPaperQuestionIdx === qIdx ? 'bg-[#1e293b] text-white border-[#1e293b]' : 'bg-white text-slate-400 border-slate-100'}`}>
                                    {qIdx + 1}
                                  </div>
                                  <div className={`mt-1.5 text-[7px] font-black px-1.5 py-0.5 rounded border uppercase tracking-widest bg-red-50 text-[#b91c1c] border-red-100`}>
                                    {q.type === 'MCQ' ? 'MCQ' : '2M'}
                                  </div>
                                </div>

                                {/* Col 2: Pedagogy */}
                                <div className="min-w-0 self-center">
                                  <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1 lg:hidden">PEDAGOGY</span>
                                  <h5 className="assess-mod-paper-pedagogy text-[13px] font-black text-[#1e293b] leading-snug">{q.pedagogy || 'General Knowledge'}</h5>
                                </div>

                                {/* Col 3: Question Content */}
                                <div className="min-w-0 self-center">
                                  <span className="block text-[7px] font-black text-slate-400 uppercase tracking-widest mb-1 lg:hidden">QUESTION CONTENT</span>
                                  
                                  {editingPaperQuestionIdx === qIdx ? (
                                    <textarea 
                                      className="assess-mod-paper-question w-full bg-[#f8fafc] border border-red-100 rounded-xl px-4 py-3 text-[13px] font-bold text-[#1e293b] focus:ring-4 focus:ring-red-50 outline-none transition-all placeholder:text-slate-300 resize-none h-[100px]"
                                      value={q.question}
                                      onChange={(e) => {
                                        const updated = [...generatedPaper];
                                        updated[activePreviewSection].questions[qIdx].question = e.target.value;
                                        setGeneratedPaper(updated);
                                      }}
                                    />
                                  ) : (
                                    <h5 className="assess-mod-paper-question text-[13px] font-bold text-slate-700 leading-relaxed">{q.question}</h5>
                                  )}
                                </div>

                                {/* Col 4: Options & Actions */}
                                <div className="flex items-center gap-4 justify-end min-w-0 self-center">
                                  <div className="flex-1 min-w-0">
                                    {isMcqQuestionType(q.type) ? (
                                      <div className="grid grid-cols-2 gap-1.5">
                                        {['a', 'b', 'c', 'd'].map(letter => (
                                          <div key={letter} className="relative">
                                            {editingPaperQuestionIdx === qIdx ? (
                                              <div className="relative">
                                                <input 
                                                  className={`w-full bg-white border rounded-lg pl-7 pr-7 py-1 text-[9px] font-bold text-[#1e293b] focus:ring-2 focus:ring-emerald-100 outline-none transition-all ${isMcqCorrectOption(q, letter) ? 'border-emerald-400 ring-1 ring-emerald-50' : 'border-slate-100'}`}
                                                  value={getMcqOptionText(q, letter)}
                                                  onChange={(e) => {
                                                    const updated = [...generatedPaper];
                                                    updated[activePreviewSection].questions[qIdx][`option_${letter.toLowerCase()}`] = e.target.value;
                                                    setGeneratedPaper(updated);
                                                  }}
                                                />
                                                <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-[8px] font-black transition-all ${isMcqCorrectOption(q, letter) ? 'text-emerald-600' : 'text-slate-300'}`}>{letter.toUpperCase()}</span>
                                                <div 
                                                  onClick={() => {
                                                    const updated = [...generatedPaper];
                                                    updated[activePreviewSection].questions[qIdx].correct_answer = letter.toUpperCase();
                                                    setGeneratedPaper(updated);
                                                  }}
                                                  className={`absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border cursor-pointer transition-all flex items-center justify-center ${isMcqCorrectOption(q, letter) ? 'border-emerald-600 bg-emerald-600' : 'border-slate-200 bg-white hover:border-emerald-400'}`}
                                                >
                                                  {isMcqCorrectOption(q, letter) && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                                                </div>
                                              </div>
                                            ) : (
                                              <div
                                                className={`assess-mod-paper-option flex items-center gap-2 px-2.5 py-2 rounded-lg border text-[13px] font-bold ${
                                                  isMcqCorrectOption(q, letter)
                                                    ? 'assess-mod-mcq-option--correct'
                                                    : 'border-slate-100 bg-slate-50 text-slate-700'
                                                }`}
                                              >
                                                <span
                                                  className={`assess-mod-mcq-option-letter w-5 h-5 rounded flex items-center justify-center text-[10px] font-black shrink-0 ${
                                                    isMcqCorrectOption(q, letter) ? 'assess-mod-mcq-option-letter--correct' : ''
                                                  }`}
                                                >
                                                  {letter.toUpperCase()}
                                                </span>
                                                <span className="truncate leading-snug">{getMcqOptionText(q, letter)}</span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="min-w-0">
                                        {editingPaperQuestionIdx === qIdx ? (
                                          <input 
                                            className="w-full bg-white border border-red-100 rounded-lg px-3 py-1.5 text-[9px] font-bold text-[#1e293b] focus:ring-2 focus:ring-red-100 outline-none transition-all placeholder:text-slate-300"
                                            placeholder="Expected Answer"
                                            value={q.correct_answer || ''}
                                            onChange={(e) => {
                                              const updated = [...generatedPaper];
                                              updated[activePreviewSection].questions[qIdx].correct_answer = e.target.value;
                                              setGeneratedPaper(updated);
                                            }}
                                          />
                                        ) : (
                                          <div className="px-3 py-2 rounded-lg border border-emerald-100 bg-emerald-50/60 text-[11px] font-bold text-emerald-800 leading-snug">
                                            {q.correct_answer || '—'}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center justify-center shrink-0 self-center">
                                    {!isReadOnly && (
                                      <div className="flex items-center gap-2">
                                        {editingPaperQuestionIdx === qIdx ? (
                                          <div className="flex flex-col items-center gap-1.5">
                                            <AssessmentConfirmIconButton
                                              onClick={() => setEditingPaperQuestionIdx(null)}
                                              title="Save question"
                                            />
                                            <AssessmentCancelIconButton
                                              onClick={() => setEditingPaperQuestionIdx(null)}
                                              title="Cancel edit"
                                            />
                                          </div>
                                        ) : (
                                          <>
                                            <AssessmentEditIconButton
                                              onClick={() => setEditingPaperQuestionIdx(qIdx)}
                                              title="Edit question"
                                            />
                                            <AssessmentDeleteIconButton
                                              onClick={() => handleDeletePaperQuestion(activePreviewSection, qIdx)}
                                              title="Delete question"
                                            />
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {!isReadOnly && (
              <div className="assess-mod-template-save-fixed flex items-center justify-end">
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  className="btn-assessment btn-assessment-danger"
                >
                  Save Template
                </button>
              </div>
            )}
        </main>
      </div>

      <input 
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
      />

      <AssessmentPopup 
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
    </div>
  );
};

const styles = `
  select {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    background-image: none !important;
  }
  select::-ms-expand {
    display: none !important;
  }
  
  /* Remove number input arrows */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type=number] {
    -moz-appearance: textfield;
  }
`;

if (typeof document !== 'undefined') {
  const styleTag = document.createElement('style');
  styleTag.innerHTML = styles;
  document.head.appendChild(styleTag);
}

export default TemplateBuilder;
